const AbstractRepository = require('@/lib/Repositories/AbstractRepository');
const DataStoreNames = require('@/lib/DataStores/DataStoreNames');
const Field = require('@/lib/CustomFields/Field');
const ItemTypeEnum = require('@/lib/Items/ItemType');

class FieldRepository extends AbstractRepository {
    /**
     * @param {DataStore} dataStore
     * @param {Hydrator} hydrator
     * @param {SyncStateManager} syncStateManager
     * @param fieldResource
     */
    constructor(
        dataStore,
        hydrator,
        syncStateManager,
        fieldResource
    ) {
        super(
            dataStore,
            hydrator,
            syncStateManager
        );

        this.fieldResource = fieldResource;
    }

    /**
     * @protected
     */
    getEntityName() {
        return 'Field';
    }

    /**
     * @protected
     */
    getEntityClass() {
        return Field;
    }

    /**
     * @protected
     */
    getTableName() {
        return DataStoreNames.FIELDS;
    }

    /**
     * @protected
     * @return {{query: function(params: object, success:function, error:function)}}
     */
    getResource() {
        return this.fieldResource;
    }

    /**
     * @returns {string[]}
     */
    getApplicableItemTypes() {
        return [
            ItemTypeEnum.BUNDLE,
            ItemTypeEnum.DELIVERY_METHOD,
            ItemTypeEnum.GIFT_VOUCHER,
            ItemTypeEnum.MEMBERSHIP_PLAN,
            ItemTypeEnum.PRODUCT,
            ItemTypeEnum.TICKET,
        ];
    }

    /**
     * Returns fields that either always apply, or apply based on what's on the given line(s).
     *
     * @param {Field[]} fields
     * @param {string} level
     * @param {CartLine[]} lines
     * @param {Boolean} [isAskEarly=null]
     *
     * @return {Field[]}
     */
    filterFieldsForLines(fields, level, lines, isAskEarly = null) {
        let eventIDs = [];
        let itemIDs = [];
        let catIDs = [];

        lines.forEach((line) => {
            let event = line.getEvent();
            if (event) {
                eventIDs.push(event.ID);
            }

            let item = line.getItem();
            if (item) {
                if (!this.getApplicableItemTypes().includes(item.itemType)) {
                    // This item is of an invalid type for triggering fields - ignore it.
                    // We ignore it even if the item is specifically linked via its itemID for consistency.
                    return;
                }

                if (item.ID) {
                    itemIDs.push(item.ID);
                }
                if (item.category) {
                    catIDs.push(item.category.ID);
                } else if (item.catID) {
                    catIDs.push(item.catID);
                }
            }
        });

        return this.filterFields(fields, level, eventIDs, itemIDs, catIDs, isAskEarly);
    }

    filterFields(fields, level, eventIDs, itemIDs, catIDs, isAskEarly = null) {
        return fields.filter((field) => {
            if (level && field.level !== level) {
                return false;
            }

            if (isAskEarly !== null && field.askEarly !== isAskEarly) {
                return false;
            }

            if (field.always) {
                return true;
            }

            if (eventIDs && eventIDs.length > 0) {
                for (let e = 0; e < eventIDs.length; e++) {
                    if (field.isForEvent(eventIDs[e])) {
                        return true;
                    }
                }
            }

            if (itemIDs && itemIDs.length > 0) {
                for (let i = 0; i < itemIDs.length; i++) {
                    if (field.isForItem(itemIDs[i])) {
                        return true;
                    }
                }
            }

            if (catIDs && catIDs.length > 0) {
                for (let c = 0; c < catIDs.length; c++) {
                    if (field.isForCat(catIDs[c])) {
                        return true;
                    }
                }
            }

            return false;
        });
    }

    /**
     * @return {Promise<Field[]>}
     */
    findAllActive() {
        return super._findAllActive();
    }

    /**
     * @return {Promise<number>}
     */
    countAllActiveNonAskEarlyFields() {
        return this.dataStore
            .getStore(this.getTableName())
            // There's only an index on status, so select by status then filter by askEarly.
            .where({ status: 'Active' })
            .filter((field) => !field.askEarly)
            .count();
    }
}

module.exports = FieldRepository;
